import { getAccessToken } from "../utils";
import { ApiErrorData } from "../types/code-login-types";

// Stored next to "accessToken" (same storage) when the user signed in with an email code
export const LOGIN_METHOD_KEY = "loginMethod";
export const CODE_SESSION_ID_KEY = "codeSessionId";
export const CODE_SESSION_SECRET_KEY = "codeSessionSecret";

const CODE_LOGIN_KEYS = [LOGIN_METHOD_KEY, CODE_SESSION_ID_KEY, CODE_SESSION_SECRET_KEY];

export const isCodeLogin = (): boolean => {
  try {
    return getAccessToken(LOGIN_METHOD_KEY) === "code";
  } catch (error) {
    return false;
  }
};

export const clearCodeLogin = (): void => {
  try {
    CODE_LOGIN_KEYS.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  } catch (error) {
    console.error(error);
  }
};

export const saveCodeLogin = (keepLogin: boolean, sessionId?: string, sessionSecret?: string): void => {
  clearCodeLogin();
  try {
    const storage = keepLogin ? localStorage : sessionStorage;
    storage.setItem(LOGIN_METHOD_KEY, "code");
    if (sessionId) storage.setItem(CODE_SESSION_ID_KEY, sessionId);
    if (sessionSecret) storage.setItem(CODE_SESSION_SECRET_KEY, sessionSecret);
  } catch (error) {
    console.error(error);
  }
};

// Ends an email-code session on the server without waiting for the answer, then clears the local keys.
// Does nothing on the server for password logins. Never throws.
export const endCodeLoginSession = (): void => {
  try {
    if (isCodeLogin()) {
      const sessionId = getAccessToken(CODE_SESSION_ID_KEY);
      const sessionSecret = getAccessToken(CODE_SESSION_SECRET_KEY);
      if (sessionId && sessionSecret) {
        fetch(`${process.env.REACT_APP_API_URL}api/users/logout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ sessionId, sessionSecret }),
        }).catch(() => undefined);
      }
    }
  } catch (error) {
    console.error(error);
  }
  clearCodeLogin();
};

export const getApiErrorData = (error: unknown): ApiErrorData | undefined => {
  const data = (error as { data?: unknown } | null | undefined)?.data;
  return data && typeof data === "object" ? (data as ApiErrorData) : undefined;
};

export const getApiErrorStatus = (error: unknown): unknown => {
  return (error as { status?: unknown } | null | undefined)?.status;
};

// The backend's own message when there is one, otherwise the fallback text
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const message = getApiErrorData(error)?.message;
  return typeof message === "string" && message.trim() !== "" ? message : fallback;
};
